import * as Sentry from "@sentry/tanstackstart-react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import React, { useEffect } from "react";

export async function initSentry() {
	// Initialize Sentry in all environments (including development for toolbar)
	const isDev = import.meta.env.DEV;
	const environment =
		import.meta.env.VITE_ENVIRONMENT ?? (isDev ? "development" : "production");

	// Skip Sentry initialization if no DSN is provided
	const dsn = import.meta.env.VITE_SENTRY_DSN;
	if (!dsn) {
		console.log("Sentry DSN not provided, skipping initialization");
		return;
	}

	const sentryConfig: Sentry.BrowserOptions = {
		dsn,
		environment,

		// Adjust sample rates based on environment
		replaysSessionSampleRate: isDev ? 0.0 : 0.0,
		replaysOnErrorSampleRate: isDev ? 0.0 : 0.0,
		tracesSampleRate: isDev ? 0.1 : environment === "production" ? 0.1 : 1.0,

		// Set release version - use git commit hash or version
		release:
			import.meta.env.VITE_APP_VERSION ??
			import.meta.env.VITE_SENTRY_RELEASE ??
			`llmgenerator@${environment}`,

		// Configure integrations
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				// Additional SDK configuration goes in here, for example:
				maskAllText: true,
				blockAllMedia: true,
			}),
		],

		// Configure error filtering
		beforeSend(event) {
			// In development, allow localhost errors for debugging
			if (isDev) {
				return event;
			}

			// Filter out development/local errors in production
			if (event.request?.url?.includes("localhost")) {
				return null;
			}

			// Filter out specific errors you don't want to track
			if (event.exception?.values?.[0]?.type === "ChunkLoadError") {
				return null;
			}

			// Filter out network errors that are not actionable
			if (event.exception?.values?.[0]?.type === "NetworkError") {
				return null;
			}

			// Filter out Trigger.dev realtime SSE stream teardown errors.
			// "pause-stream" and AbortError are thrown internally when navigating
			// away while a realtime subscription is active — expected, not bugs.
			const exceptionValue = event.exception?.values?.[0];
			if (
				exceptionValue?.value === "pause-stream" ||
				(exceptionValue?.type === "AbortError" &&
					exceptionValue?.value?.includes("signal is aborted without reason"))
			) {
				return null;
			}

			// Filter out browser extension noise. "Object Not Found Matching Id"
			// is emitted by password manager extensions (1Password, LastPass, etc.)
			// when they try to interact with form fields that no longer exist after
			// navigation. This is entirely outside our code.
			if (exceptionValue?.value?.includes("Object Not Found Matching Id")) {
				return null;
			}

			return event;
		},

		// Add user context
		initialScope: {
			tags: {
				component: "client",
				runtime: "browser",
				environment,
			},
		},
	};

	Sentry.init(sentryConfig);

	// Load feedback integration
	Sentry.lazyLoadIntegration("feedbackIntegration")
		.then((feedbackIntegration) => {
			Sentry.addIntegration(
				feedbackIntegration({
					// User Feedback configuration options
				}),
			);
		})
		.catch(() => {
			// this can happen if e.g. a network error occurs,
			// in this case User Feedback will not be enabled
		});
}

// React hook for Sentry Toolbar (use this in your React components)
export function useSentryToolbar() {
	useEffect(() => {
		const isDev = import.meta.env.DEV;

		// Only initialize toolbar in development
		if (isDev) {
			let cleanup: (() => void) | undefined;

			// Dynamically import and initialize the toolbar
			import("@sentry/toolbar")
				.then(({ useSentryToolbar: initToolbar }) => {
					initToolbar({
						enabled: true,
						initProps: {
							organizationSlug: "llm-generator",
							projectIdOrSlug: "llmgenerator",
							placement: "bottom-right-corner",
							theme: "system",
						},
					});
					console.log("Sentry Toolbar initialized for development");
				})
				.catch((error) => {
					console.log("Sentry Toolbar not available:", error);
				});

			// Cleanup function
			return () => {
				if (cleanup) {
					cleanup();
				}
			};
		}
	}, []);
}

// Helper function to capture exceptions with context
export function captureClientException(
	error: Error,
	context?: Record<string, unknown>,
) {
	Sentry.withScope((scope) => {
		if (context) {
			scope.setContext("additional", context);
		}
		scope.setTag("component", "client");
		Sentry.captureException(error);
	});
}

// Helper function to capture messages
export function captureClientMessage(
	message: string,
	level: "info" | "warning" | "error" = "info",
	context?: Record<string, unknown>,
) {
	Sentry.withScope((scope) => {
		if (context) {
			scope.setContext("additional", context);
		}
		scope.setTag("component", "client");
		Sentry.captureMessage(message, level);
	});
}

// Helper function to set user context
export function setSentryUser(user: {
	id: string;
	email?: string;
	username?: string;
}) {
	Sentry.setUser({
		id: user.id,
		email: user.email,
		username: user.username,
	});
}

// Helper function to add breadcrumb
export function addSentryBreadcrumb(
	message: string,
	category: string,
	data?: Record<string, unknown>,
) {
	Sentry.addBreadcrumb({
		message,
		category,
		data,
		timestamp: Date.now() / 1000,
	});
}

class MyErrorBoundary extends React.Component<
	{ children: React.ReactNode },
	{ hasError: boolean; error?: Error }
> {
	constructor(props: { children: React.ReactNode }) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error) {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error("Error boundary caught an error:", error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			return (
				<div className="flex min-h-screen items-center justify-center">
					<div className="text-center">
						<h1 className="mb-4 font-bold text-2xl text-red-600">
							Something went wrong
						</h1>
						<p className="mb-4 text-gray-600">
							We've been notified about this error and will fix it soon.
						</p>
						<button
							type="button"
							onClick={() => window.location.reload()}
							className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
						>
							Reload Page
						</button>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

export const MySentryWrappedErrorBoundary = Sentry.withErrorBoundary(
	MyErrorBoundary,
	{
		fallback: ({ error, resetError }) => (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-center">
					<h1 className="mb-4 font-bold text-2xl text-red-600">
						Application Error
					</h1>
					<p className="mb-4 text-gray-600">
						{(error as Error)?.message ?? "An unexpected error occurred"}
					</p>
					<button
						type="button"
						onClick={resetError}
						className="mr-2 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
					>
						Try Again
					</button>
					<button
						type="button"
						onClick={() => window.location.reload()}
						className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
					>
						Reload Page
					</button>
				</div>
			</div>
		),
	},
);

// TanStack Router Error Component Factory
export function createRouteErrorComponent(context?: {
	routePath?: string;
	routeName?: string;
	customMessage?: string;
}) {
	return function RouteErrorComponent(props: ErrorComponentProps) {
		const { error, reset } = props;
		useEffect(() => {
			// Capture the error with route context
			Sentry.withScope((scope) => {
				// Add route-specific context
				if (context?.routePath) {
					scope.setTag("route.path", context.routePath);
				}
				if (context?.routeName) {
					scope.setTag("route.name", context.routeName);
				}

				// Add additional context
				scope.setContext("route", {
					path: context?.routePath,
					name: context?.routeName,
					url: window.location.href,
					timestamp: new Date().toISOString(),
				});

				scope.setLevel("error");
				scope.setTag("component", "route-error");

				// Capture the exception
				Sentry.captureException(error);
			});
		}, [error, context?.routePath, context?.routeName]);

		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="mx-auto max-w-md p-6 text-center">
					<h1 className="mb-4 font-bold text-2xl text-red-600">
						{context?.customMessage || "Page Error"}
					</h1>
					<p className="mb-4 text-gray-600">
						{import.meta.env.DEV
							? error.message
							: "We've encountered an error on this page and our team has been notified."}
					</p>
					<div className="space-x-2">
						{reset && (
							<button
								type="button"
								onClick={reset}
								className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
							>
								Try Again
							</button>
						)}
						<button
							type="button"
							onClick={() => window.history.back()}
							className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
						>
							Go Back
						</button>
					</div>
				</div>
			</div>
		);
	};
}

// Convenience function for simple route error components
export function createSimpleRouteErrorComponent(routeName: string) {
	return createRouteErrorComponent({
		routeName,
		customMessage: `Error in ${routeName}`,
	});
}
